import type {
  LeadFormFieldDto,
  ProfileModuleDto,
} from '@up2stay/up2promo-api-client';

import type { ProfileFormData } from '@/core/components/profile/edit/profile-edit-form-schema';
import type { UpdateProfileInput } from '@/core/hooks/use-profile';
import {
  buildProfileStylingPayload,
  PROFILE_STYLING_FORM_DEFAULTS,
  type ProfileStylingFormSlice,
} from '@/core/lib/profile-styling-form';

function stylingSliceFromForm(
  formValues: ProfileFormData,
  resolvedAccentColor: string | null | undefined,
): ProfileStylingFormSlice {
  return {
    layout: formValues.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
    theme: formValues.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
    headerStyle:
      formValues.headerStyle ?? PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
    headerImage: formValues.headerImage ?? null,
    buttonShape: formValues.buttonShape ?? null,
    accentColor: resolvedAccentColor ?? null,
  };
}

export function buildProfileEditUpdatePayload(options: {
  userTitle: string;
  formValues: ProfileFormData;
  modules: ProfileModuleDto[];
  fixedLeadFormFields: LeadFormFieldDto[];
  resolvedAccentColor: string | null | undefined;
  resolvedShowUp2promoBranding: boolean;
}): UpdateProfileInput {
  const {
    userTitle,
    formValues,
    modules,
    fixedLeadFormFields,
    resolvedAccentColor,
    resolvedShowUp2promoBranding,
  } = options;

  // Always include leadFormEnabled and all lead-form settings so they are kept when disabled
  const leadFormEnabled = formValues.leadFormEnabled ?? false;
  const visibilityMode = formValues.leadFormVisibilityMode ?? 'manual';

  // Get autoOpenDelay value - ensure it's always a number (0 if null/undefined)
  const autoOpenDelayValue =
    typeof formValues.leadFormAutoOpenDelay === 'number'
      ? formValues.leadFormAutoOpenDelay
      : (formValues.leadFormAutoOpenDelay ?? 0);

  const leadFormData: UpdateProfileInput = {
    leadFormEnabled,
    leadFormVisibilityMode: visibilityMode,
    leadFormAutoOpenDelay: autoOpenDelayValue,
    leadFormFields: fixedLeadFormFields,
    leadsAutoShareWithWorkspace:
      formValues.leadsAutoShareWithWorkspace ?? false,
  };

  // Leere Felder explizit mitsenden (""), sonst fehlt das Feld im Request und das Backend behält den alten Wert
  return {
    title: userTitle,
    jobTitle: (formValues.jobTitle ?? '').trim(),
    company: (formValues.company ?? '').trim(),
    location: (formValues.location ?? '').trim(),
    showProfileImage: formValues.showProfileImage ?? true,
    showUp2promoBranding: resolvedShowUp2promoBranding,
    language: formValues.language,
    visibility: formValues.visibility,
    modules: modules,
    styling: buildProfileStylingPayload(
      stylingSliceFromForm(formValues, resolvedAccentColor),
    ),
    ...leadFormData,
  };
}
